import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Stack,
  Paper,
  Group,
  ActionIcon,
  TextInput,
  Alert,
} from '@mantine/core';
import {
  IconGripVertical,
  IconTrash,
  IconListCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '../../../types';
import type { InlineRequirement } from '../../tasks/wizard/types/data/InlineRequirement';
import { isInlineRequirement } from '../../tasks/wizard/steps/Step3Requirements/Step3Requirements.types';
import type { DragDropResult } from '../../tasks/wizard/steps/Step3Requirements/Step3Requirements.types';
import { EmptyState } from '@/components/common/EmptyState';
import { DND_POINTER_ACTIVATION, DND_DROP_ANIMATION } from '@/constants/dnd';
import classes from './RequirementsList.module.css';

interface RequirementsListProps {
  requirements: (Task['requirements'][0] | InlineRequirement)[];
  onUpdateRequirement: (index: number, text: string) => void;
  onRemoveRequirement: (index: number) => void;
  onDragEnd: (result: DragDropResult) => void;
  isLoading?: boolean;
  formError?: ReactNode;
}

function getRequirementKey(
  requirement: Task['requirements'][0] | InlineRequirement
): string {
  return (
    requirement.id ||
    (isInlineRequirement(requirement) ? requirement.tempId : '') ||
    ''
  );
}

interface SortableRequirementItemProps {
  id: string;
  index: number;
  requirement: Task['requirements'][0] | InlineRequirement;
  isLoading: boolean;
  onUpdate: (index: number, text: string) => void;
  onRemove: (index: number) => void;
}

function SortableRequirementItem({
  id,
  index,
  requirement,
  isLoading,
  onUpdate,
  onRemove,
}: SortableRequirementItemProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isLoading });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      className={`${classes.requirementItem} ${isDragging ? classes.requirementItemPlaceholder : ''}`}
    >
      <Group className={classes.requirementItemGroup}>
        <ActionIcon
          variant="subtle"
          size="sm"
          className={
            isLoading ? classes.dragHandleDisabled : classes.dragHandle
          }
          {...attributes}
          {...listeners}
        >
          <IconGripVertical size={16} />
        </ActionIcon>

        <TextInput
          value={requirement.text}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onUpdate(index, e.currentTarget.value)
          }
          disabled={isLoading}
          className={classes.requirementInput}
          variant="unstyled"
          placeholder={t('wizard.step3.requirementDescription')}
          autoComplete="off"
        />

        <ActionIcon
          variant="subtle"
          color="red"
          onClick={() => onRemove(index)}
          disabled={isLoading}
          className={classes.deleteButton}
          aria-label={`Remove requirement: ${requirement.text}`}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Paper>
  );
}

export function RequirementsList({
  requirements,
  onUpdateRequirement,
  onRemoveRequirement,
  onDragEnd,
  isLoading = false,
  formError,
}: RequirementsListProps): React.ReactElement {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: DND_POINTER_ACTIVATION,
    }),
    useSensor(KeyboardSensor)
  );

  const items = useMemo(
    () => requirements.map(getRequirementKey),
    [requirements]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.indexOf(String(active.id));
      const newIndex = items.indexOf(String(over.id));

      onDragEnd({
        source: { index: oldIndex },
        destination: { index: newIndex },
      });
    },
    [items, onDragEnd]
  );

  const handleDragCancel = useCallback(() => setActiveId(null), []);

  if (requirements.length === 0) {
    return (
      <>
        <EmptyState
          icon={<IconListCheck size={32} />}
          title={t('wizard.step3.noRequirementsYet')}
          description={t('wizard.step3.defineInspectionCheckpoints')}
          hint={t('wizard.step3.startTypingToSee')}
          size="sm"
        />
        {formError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mt="md">
            {formError}
          </Alert>
        )}
      </>
    );
  }

  const activeRequirement = activeId
    ? requirements.find((r) => getRequirementKey(r) === activeId)
    : null;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <Stack className={classes.requirementsList}>
            {requirements.map((req, index) => {
              const key = getRequirementKey(req);
              return (
                <SortableRequirementItem
                  key={key}
                  id={key}
                  index={index}
                  requirement={req}
                  isLoading={isLoading}
                  onUpdate={onUpdateRequirement}
                  onRemove={onRemoveRequirement}
                />
              );
            })}
          </Stack>
        </SortableContext>

        {createPortal(
          <DragOverlay dropAnimation={DND_DROP_ANIMATION} zIndex={9999}>
            {activeRequirement ? (
              <Paper
                className={`${classes.requirementItem} ${classes.requirementItemDragging}`}
              >
                <Group className={classes.requirementItemGroup}>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className={classes.dragHandle}
                  >
                    <IconGripVertical size={16} />
                  </ActionIcon>
                  <TextInput
                    value={activeRequirement.text}
                    readOnly
                    className={classes.requirementInput}
                    variant="unstyled"
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    className={`${classes.deleteButton} ${classes.deleteButtonHidden}`}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Paper>
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mt="md">
          {formError}
        </Alert>
      )}
    </>
  );
}
